import { FC, useEffect, useMemo } from "react";
import { FormHelperText, InputLabel, Link, Stack, StackProps } from "@mui/material";
import { GetFieldIsValidAndErrorMessage, DeleteField } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import useDateValidator, { DateValidatorProps } from "../../../Utils/Hooks/Validators/useDateValidator";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { DatePicker, DatePickerProps, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { useTranslation } from "react-i18next";

interface FormInputDateProps extends Omit<DatePickerProps<any>, "id" | "label" | "error" | "type" | "renderInput"> {
  label?: string;
  validationProps: DateValidatorProps;
  stackProps?: StackProps;
  hideReset?: boolean;
  format?: string;
  onReset?: () => void;
  onChange?: (value: Date) => void;
}

const FormInputDate: FC<FormInputDateProps> = (props) => {
  const { t } = useTranslation();
  const {
    label,
    validationProps,
    onChange,
    onReset,
    stackProps,
    hideReset = false,
    format = GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS,
    ...otherProps
  } = props;
  const validateDate = useDateValidator(validationProps);
  const dispatch = useAppDispatch();

  const errorMessage = useAppSelector(GetFieldIsValidAndErrorMessage(validationProps.formId, validationProps.fieldId));
  const isValid = !errorMessage;

  const [isValidMemo, errorMessageMemo] = useMemo(() => {
    return [isValid, errorMessage];
  }, [isValid, errorMessage]);

  const handleOnChange = (value: any) => {
    onChange && onChange(value);
    validateDate(value as Date);
  };

  const handleOnReset = () => {
    onReset && onReset();
  };

  useEffect(() => {
    validateDate(props.value);
  }, [
    validationProps.required,
    validationProps.after,
    validationProps.before,
    validationProps.notAfter,
    validationProps.notBefore,
    validationProps.onlyInFuture,
  ]);

  useEffect(() => {
    return () => {
      dispatch(
        DeleteField({
          formId: validationProps.formId,
          formSectionId: validationProps.formSectionId,
          fieldId: validationProps.fieldId,
        })
      );
    };
  }, []);

  return (
    <Stack {...stackProps}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: -2 }}>
        {label && (
          <InputLabel htmlFor={`${validationProps.fieldId}-label`} required={validationProps.required}>
            {label}
          </InputLabel>
        )}
        {!hideReset && (
          <Link
            disabled={!props.value}
            component="button"
            color="primary.text"
            underline="hover"
            onClick={() => handleOnReset()}
          >
            {t("common:reset")}
          </Link>
        )}
      </Stack>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          onChange={(newValue) => {
            handleOnChange(newValue!);
          }}
          format={format}
          //renderInput={(params) => <TextField sx={{}} {...params} />}
          {...otherProps}
        />
      </LocalizationProvider>
      {!isValidMemo && <FormHelperText error>{errorMessageMemo}</FormHelperText>}
    </Stack>
  );
};

export default FormInputDate;
